import type { DocNode, HuntPlanTeamDocument } from '@wehunt/ui'

/**
 * Resolve the ids a hunt stored for documents attached from the team's own
 * documents into the documents themselves.
 *
 * A hunt never copies those files — it references them — so the ids are only
 * meaningful against the team's *current* documents. Two cases therefore yield
 * nothing rather than a placeholder: the document was deleted after the hunt
 * attached it, and the listing hasn't loaded yet (`docTree` still undefined).
 * Rendering nothing beats naming a file nobody can open, or showing chips that
 * would change under the reader mid-load.
 *
 * A document's id is its path relative to the team's documents root (that's how
 * `buildDocTree` keys the tree), so the storage path is that root plus the id.
 */
export function attachedTeamDocuments(
  groupId: string,
  docTree: Record<string, DocNode> | undefined,
  attachmentDocumentIds: readonly string[] | undefined,
): HuntPlanTeamDocument[] {
  if (!docTree || !attachmentDocumentIds?.length) return []

  return attachmentDocumentIds.flatMap((id) => {
    const node = docTree[id]
    if (node?.type !== 'file') return []
    return [{
      id: node.id,
      name: node.name,
      kind: node.kind,
      storagePath: `documents/${groupId}/${node.id}`,
    }]
  })
}
